import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useMemo } from 'react';
import { EquipmentType, TEMPERATURE_CONTROLLED_TYPES } from '../../../config/equipment-types';
import type { IEquipmentService } from '../../../services/equipment';
import { EquipmentService } from '../../../services/equipment';
import type { IFryerService } from '../../../services/fryer';
import { FryerService } from '../../../services/fryer';
import type { IMetalDetectorService } from '../../../services/metal-detector/metal-detector.service';
import { MetalDetectorService } from '../../../services/metal-detector/metal-detector.service';
import type { IPestControlService } from '../../../services/pest-control/pest-control.service';
import { PestControlService } from '../../../services/pest-control/pest-control.service';
import {
  equipmentQueryKeys,
  fryerQueryKeys,
  metalDetectorQueryKeys,
  pestControlQueryKeys,
} from '../query-keys';

export type EquipmentTab =
  | 'refrigerators'
  | 'hot-holding'
  | 'fryers'
  | 'pest-control'
  | 'metal-detection';

export function useEquipmentList() {
  const service = useInjection<IEquipmentService>(EquipmentService);

  const {
    data: equipmentList = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: equipmentQueryKeys.list(),
    queryFn: () => service.findAll(),
  });

  // "Refrigerators" tab shows temperature-controlled equipment EXCLUDING hot
  // holding, which gets its own tab (same query, split client-side like the
  // other equipment tabs).
  const filteredEquipmentList = useMemo(
    () =>
      equipmentList.filter(
        (eq) =>
          TEMPERATURE_CONTROLLED_TYPES.includes(eq.type as EquipmentType) &&
          eq.type !== EquipmentType.MantenimentoCaldo,
      ),
    [equipmentList],
  );

  const hotHoldingList = useMemo(
    () => equipmentList.filter((eq) => eq.type === EquipmentType.MantenimentoCaldo),
    [equipmentList],
  );

  return { equipmentList, filteredEquipmentList, hotHoldingList, isLoading, error };
}

export function useFryerList() {
  const service = useInjection<IFryerService>(FryerService);

  const {
    data: fryerList = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: fryerQueryKeys.list(),
    queryFn: () => service.findAll(),
  });

  return { fryerList, isLoading, error };
}

export function usePestControlPointList() {
  const service = useInjection<IPestControlService>(PestControlService);

  const {
    data: pestControlPointList = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: pestControlQueryKeys.list(),
    queryFn: () => service.findAll(),
  });

  return { pestControlPointList, isLoading, error };
}

export function useMetalDetectorList() {
  const service = useInjection<IMetalDetectorService>(MetalDetectorService);

  const {
    data: metalDetectorList = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: metalDetectorQueryKeys.list(),
    queryFn: () => service.findAll(),
  });

  return { metalDetectorList, isLoading, error };
}

/**
 * Food-preset catalog for the equipment editor's dropdown: the 7 system
 * presets (backend static config) plus any tenant-declared custom presets.
 * Unlike the system-only catalog, tenants can add/edit/delete entries
 * (`use-food-preset-mutations.ts`), so this is no longer treated as
 * effectively-static data — default staleTime, same as the other list
 * queries in this file.
 */
export function useFoodPresets() {
  const service = useInjection<IEquipmentService>(EquipmentService);

  return useQuery({
    queryKey: equipmentQueryKeys.foodPresets(),
    queryFn: () => service.listFoodPresets(),
  });
}
